import { Discovery } from './Discovery';
import { Introduction } from './Introduction';
import { Planning } from './Planning';
import { Action } from './Action';
import { Reflect } from './Reflect';
import {
  HubStatusEnum,
  ProgressStepStatusEnum,
  ProjectHubResponse,
  SimplifiedUserRole,
  HubProgressResponse,
} from '@platform/client/schemas';
import { HubProgressHeader } from '../components/HubProgressHeader';
import { useEffect, useState } from 'react';
import { PlanningProjectPlan } from './PlanningProjectPlan';

export const ProgressAccordion = ({ hubData }: { hubData: ProjectHubResponse }) => {
  const [openAccordion, setOpenAccordion] = useState<string>();
  const {
    matchGroupId,
    progress,
    userType,
    project,
    status,
    introduction,
    planningGuideUrl,
    surveyUrl,
    storyUrl,
    teamId,
  } = hubData;
  const matchFailed = progress?.discovery === ProgressStepStatusEnum.failure;
  const isTeam = !!teamId;
  const filteredProgress = getFilteredProgress(progress, isTeam);
  const completedSteps = Object.values(filteredProgress).filter(
    (status) =>
      status === ProgressStepStatusEnum.success ||
      status === ProgressStepStatusEnum.inactive_in_progress,
  ).length;
  const totalSteps = Object.keys(filteredProgress).length;
  const displayPlanUi = hubData?.displayPlanUi ?? true;

  const userCurrentStep = Object.entries(filteredProgress).find(
    ([, status]) =>
      status === ProgressStepStatusEnum.active_in_progress ||
      status === ProgressStepStatusEnum.inactive_in_progress,
  )?.[0];
  useEffect(() => {
    if (userCurrentStep) {
      setOpenAccordion(userCurrentStep);
    }
  }, [userCurrentStep]);

  const onAccordionToggle = (accordionKey: string) => (isOpen: boolean) => {
    if (isOpen) {
      setOpenAccordion(accordionKey);
    }
  };

  return (
    <>
      {!matchFailed && (
        <HubProgressHeader
          completed={completedSteps}
          total={totalSteps}
          project={project}
          includeDates={progress.discovery === ProgressStepStatusEnum.success}
          allowDateEdit={
            userType === SimplifiedUserRole.PROFESSIONAL && status === HubStatusEnum.Matched
          }
          isTeam={isTeam}
        />
      )}
      {introduction && filteredProgress.introduction && (
        <Introduction
          status={ProgressStepStatusEnum.success}
          userType={userType}
          introduction={introduction}
          isOpen={openAccordion === 'introduction'}
          onToggle={onAccordionToggle('introduction')}
        />
      )}
      {hubData.discovery && filteredProgress.discovery && hubData.professionals && (
        <Discovery
          status={progress.discovery}
          isTeam={isTeam}
          userType={userType}
          discovery={hubData.discovery}
          discoveryModalData={{
            matchGroupId,
            assignmentDiscoveryId: 0,
            otherPartyName:
              userType === SimplifiedUserRole.PROFESSIONAL ?
                hubData.organization.name
              : `${hubData.professionals[0].firstname} ${hubData.professionals[0].lastname}`,
            userType: userType,
            projectId: project?.id,
            projectName: project?.name,
          }}
          isOpen={openAccordion === 'discovery'}
          onToggle={onAccordionToggle('discovery')}
        />
      )}
      {!displayPlanUi ?
        <Planning
          status={progress.planning}
          isTeam={isTeam}
          userType={userType}
          projectId={project?.id}
          planningGuideUrl={planningGuideUrl}
          isOpen={openAccordion === 'planning'}
          onToggle={onAccordionToggle('planning')}
        />
      : <PlanningProjectPlan
          status={progress.planning}
          isTeam={isTeam}
          userType={userType}
          projectId={project?.id}
          projectPlanId={hubData.projectPlanId}
          isOpen={openAccordion === 'planning'}
          onToggle={onAccordionToggle('planning')}
        />
      }
      <Action
        status={progress.action}
        isTeam={isTeam}
        userType={userType}
        projectId={hubData.project?.id}
        planningGuideUrl={planningGuideUrl}
        isOpen={openAccordion === 'action'}
        onToggle={onAccordionToggle('action')}
        hubActionData={hubData.action}
        isProjectPlanMatch={displayPlanUi}
      />
      <Reflect
        status={progress.reflect}
        isTeam={isTeam}
        userType={userType}
        surveyUrl={surveyUrl}
        storyUrl={storyUrl}
        isOpen={openAccordion === 'reflect'}
        onToggle={onAccordionToggle('reflect')}
      />
    </>
  );
};

/** Team matches only show the planning, action, and reflect steps. This may also end up aplying to
 * individual assigned cohorts
 */
const getFilteredProgress = (
  progress: HubProgressResponse,
  isTeam: boolean,
): Partial<HubProgressResponse> => {
  const TEAM_STEPS: (keyof HubProgressResponse)[] = ['planning', 'action', 'reflect'];
  return Object.fromEntries(
    (Object.keys(progress) as (keyof HubProgressResponse)[])
      .filter((key) => !isTeam || TEAM_STEPS.includes(key))
      .map((key) => [key, progress[key]]),
  );
};
